import { memo } from "react";
import { cn } from "utils";
import { withViewMore, ViewMoreProps, ViewMoreState } from "./withViewMore";
import NextImage from "@/src/components/NextImage";

interface TeamMember {
  display_name?: string;
  avatar?: string;
  roles?: string[];
}

export interface CoreTeamProps extends ViewMoreProps {
  /** 团队成员数据 */
  teamMembers?: TeamMember[];
  /** 成员项自定义类名 */
  memberItemClassName?: string;
  /** 成员头像宽度 */
  imageWidth?: number;
  /** 成员头像高度 */
  imageHeight?: number;
  /** 成员名称自定义类名 */
  nameClassName?: string;
  /** 成员角色自定义类名 */
  roleClassName?: string;
}

interface CoreTeamComponentProps extends CoreTeamProps {
  viewMoreState: ViewMoreState;
  data: TeamMember[];
}

// 核心团队组件（内部实现）
const CoreTeamComponent = (props: CoreTeamComponentProps) => {
  const {
    viewMoreState,
    data,
    memberItemClassName,
    imageWidth = 40,
    imageHeight = 40,
    nameClassName,
    roleClassName,
    containerClassName,
  } = props;

  const { containerRef, itemRef, containerHeight, enableViewMore } =
    viewMoreState;

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex flex-wrap flex-row gap-4 relative",
        enableViewMore && "overflow-hidden transition-all duration-300",
        containerClassName,
      )}
      style={enableViewMore ? { height: containerHeight } : undefined}
    >
      {data.map((member, index) => (
        <div
          key={index}
          ref={index === 0 ? itemRef : undefined}
          className={cn(
            "flex items-center justify-center py-2 px-3 rounded-[12px] bg-background_white_overlay5 gap-3",
            memberItemClassName,
          )}
        >
          <NextImage
            src={member.avatar}
            alt={member.display_name}
            className="rounded-full"
            width={imageWidth}
            height={imageHeight}
          />
          <div className="flex flex-col">
            <span
              className={cn(
                "body_medium_m text-content_primary",
                nameClassName,
              )}
            >
              {member.display_name}
            </span>
            <span
              className={cn(
                "body_medium_s text-content_tertiary",
                roleClassName,
              )}
            >
              {member?.roles?.[0]}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// 使用高阶组件增强CoreTeam
const CoreTeam = withViewMore<CoreTeamProps, TeamMember>(
  CoreTeamComponent,
  "Core Team",
  (props) => props.teamMembers,
);

export default memo(CoreTeam);
